import React, { useEffect, useMemo, useState } from 'react';
import type { Customer } from '../../shared/types';
import { SEGMENT_PALETTE, NO_SEGMENT_PALETTE, segmentLabel } from '../../shared/customerSegmentation';
import { getBackendService } from '../../shared/services/apiConfig';
import { visitAmountCents } from '../../shared/visitAmount';
import { ChartLineIcon, ClockIcon, UtensilsIcon, ScrollIcon } from './icons/UiIcons';
import { safeStorageImageUrl } from '../../shared/restaurantShowcase';

const HISTORY_PAGE_SIZE = 20;

interface VisitEvent {
    id: string;
    date: Date;
    method: string;
    pointsEarned: number;
    amount?: number | null;
}

const formatHistoryDate = (d: Date) =>
    d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
const formatHistoryTime = (d: Date) =>
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

const formatLastVisit = (c: Customer): string => {
    if (c.daysSinceLastVisit == null) return 'Jamais venu';
    if (c.daysSinceLastVisit === 0) return "Aujourd'hui";
    if (c.daysSinceLastVisit === 1) return 'Hier';
    return `Il y a ${c.daysSinceLastVisit} jours`;
};

const formatMembershipDuration = (joinedDate?: string): string | null => {
    if (!joinedDate) return null;
    const d = new Date(joinedDate);
    if (Number.isNaN(d.getTime())) return null;
    const diffDays = Math.floor((Date.now() - d.getTime()) / (24 * 60 * 60 * 1000));
    if (diffDays < 7) return 'Inscrit cette semaine';
    if (diffDays < 31) return `Client depuis ${diffDays} jours`;
    const months = Math.floor(diffDays / 30);
    if (months < 12) return `Client depuis ${months} mois`;
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    return remMonths > 0 ? `Client depuis ${years} an${years > 1 ? 's' : ''} et ${remMonths} mois` : `Client depuis ${years} an${years > 1 ? 's' : ''}`;
};

const initialsOf = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '??';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const colorFromName = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    const palette = ['bg-sky-500', 'bg-emerald-500', 'bg-amber-500', 'bg-purple-500', 'bg-rose-500', 'bg-indigo-500', 'bg-teal-500'];
    return palette[Math.abs(hash) % palette.length];
};

const Avatar: React.FC<{ customer: Customer }> = ({ customer }) => {
    const safeAvatar = safeStorageImageUrl(customer.avatarUrl);
    if (safeAvatar) {
        return <img className="h-20 w-20 rounded-full object-cover" src={safeAvatar} alt={customer.name} />;
    }
    return (
        <div className={`h-20 w-20 ${colorFromName(customer.name)} rounded-full text-white text-2xl font-bold flex items-center justify-center`}>
            {initialsOf(customer.name)}
        </div>
    );
};

const Tile: React.FC<{ label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ label, value, sub }) => (
    <div className="bg-gray-50 rounded-lg p-4">
        <p className="text-xs text-gray-400 font-semibold">{label}</p>
        <p className="mt-1 text-2xl text-gray-900 dark:text-gray-100 font-stat">{value}</p>
        {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
);

/**
 * Bar chart minimaliste pour activité 6 derniers mois.
 * Affiché uniquement si ≥ 2 mois ont au moins 1 visite (cf. spec : pas de
 * graphique gabarit, seulement quand on a vraiment de la matière).
 */
const ActivityBars: React.FC<{ data: { month: string; visits: number }[] }> = ({ data }) => {
    const max = Math.max(1, ...data.map(d => d.visits));
    return (
        <div className="flex items-end gap-2 h-32">
            {data.map((d, i) => {
                const heightPct = (d.visits / max) * 100;
                return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex-1 flex items-end">
                            <div
                                className="w-full bg-indigo-400 rounded-t-md transition-all"
                                style={{ height: `${Math.max(heightPct, d.visits > 0 ? 4 : 0)}%` }}
                                title={`${d.visits} visite${d.visits > 1 ? 's' : ''}`}
                            />
                        </div>
                        <div className="text-xs text-gray-500">{d.month}</div>
                        <div className="text-xs font-semibold text-gray-700 tabular-nums">{d.visits}</div>
                    </div>
                );
            })}
        </div>
    );
};

/**
 * Section historique des visites uniquement (pas les réservations — cf. spec :
 * "Pas de réservations mélangées dedans").
 */
const VisitsHistory: React.FC<{ customerId: number | string }> = ({ customerId }) => {
    const [events, setEvents] = useState<VisitEvent[] | null>(null);
    const [error, setError] = useState(false);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const backend = await getBackendService();
                const user = backend.getCurrentUser();
                if (!user?.restaurantId) {
                    if (isMounted) setEvents([]);
                    return;
                }
                if (!(backend as any).getCustomerHistory) {
                    if (isMounted) setEvents([]);
                    return;
                }
                const result = await (backend as any).getCustomerHistory(
                    String(customerId),
                    user.restaurantId,
                );
                // Filtrer pour ne garder que les visites, ignorer les réservations.
                const visits: VisitEvent[] = (result || [])
                    .filter((e: any) => e.kind === 'visit')
                    .map((e: any) => ({
                        id: e.id,
                        date: e.date instanceof Date ? e.date : new Date(e.date),
                        method: e.data?.validationMethod || 'visit',
                        pointsEarned: typeof e.data?.pointsEarned === 'number' ? e.data.pointsEarned : 0,
                        // Centimes (visitAmountCents : anciennes visites de caisse en euros comprises).
                        amount: visitAmountCents(e.data),
                    }))
                    .sort((a: VisitEvent, b: VisitEvent) => b.date.getTime() - a.date.getTime());
                if (isMounted) setEvents(visits);
            } catch (e) {
                console.error('Failed to load customer history', e);
                if (isMounted) setError(true);
            }
        };
        load();
        return () => { isMounted = false; };
    }, [customerId]);

    if (error) return <p className="text-sm text-gray-400 italic">Historique indisponible.</p>;
    if (events === null) return <p className="text-sm text-gray-400">Chargement…</p>;
    if (events.length === 0) return <p className="text-sm text-gray-400 italic">Aucune visite enregistrée.</p>;

    const visible = showAll ? events : events.slice(0, HISTORY_PAGE_SIZE);
    const remaining = events.length - visible.length;

    const methodLabel = (m: string): string => {
        // qr_wallet, qr_reservation, qr_pending, qr_pending_reservation : QR wallet (secours du NFC)
        if (m === 'qr' || m.startsWith('qr_')) return 'QR Wallet';
        if (m === 'nfc' || m === 'nfc_reservation') return 'Scan NFC';
        if (m === 'nfc_pos' || m === 'nfc_pending' || m === 'nfc_pending_reservation') return 'NFC POS';
        if (m === 'manual' || m.startsWith('manual_')) return 'Validation manuelle';
        if (m === 'pos' || m.startsWith('pos_')) return 'Caisse';
        // Addition gardée sur la page de la puce par un client sans l'app, récupérée à son inscription.
        if (m === 'guest_claim') return 'Addition (récupérée à l\'inscription)';
        return 'Visite';
    };

    return (
        <div>
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
                {visible.map(ev => (
                    <li key={ev.id} className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50">
                        <span className="text-gray-400 text-sm shrink-0 tabular-nums">
                            {formatHistoryDate(ev.date)} · {formatHistoryTime(ev.date)}
                        </span>
                        <span className="text-sm text-gray-700 flex-1 truncate">
                            {methodLabel(ev.method)}
                        </span>
                        {typeof ev.amount === 'number' && ev.amount > 0 && (
                            <span className="text-sm font-semibold text-gray-900 tabular-nums shrink-0">
                                {(ev.amount / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </span>
                        )}
                        {ev.pointsEarned > 0 && (
                            <span className="text-xs px-2 py-0.5 rounded bg-green-50 text-green-700 font-semibold shrink-0">
                                +{ev.pointsEarned} pts
                            </span>
                        )}
                    </li>
                ))}
            </ul>
            {remaining > 0 && (
                <button
                    onClick={() => setShowAll(true)}
                    className="mt-3 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                >
                    Charger {remaining} de plus
                </button>
            )}
        </div>
    );
};

const CustomerProfile: React.FC<{ customer: Customer; onBack: () => void }> = ({ customer, onBack }) => {
    const palette = customer.segment ? SEGMENT_PALETTE[customer.segment] : NO_SEGMENT_PALETTE;

    // Section "Activité 6 derniers mois" : uniquement si ≥ 2 mois actifs.
    const last6Months = useMemo(() => {
        const arr = customer.visitsByMonth.slice(-6);
        const activeMonths = arr.filter(m => m.visits > 0).length;
        return activeMonths >= 2 ? arr : null;
    }, [customer.visitsByMonth]);

    // Habitudes : on ne montre pas tant qu'on n'a pas ≥ 5 visites ET la donnée
    // calculée proprement côté serveur. Aujourd'hui ce n'est pas le cas → masqué.
    // (cf. deferred-work : data pipeline pour calculer preferredDay/Service depuis visits)
    const habitsAvailable = false;

    // Top plats : affiché uniquement s'il y a vraiment des plats remontés (posOrders.customerId peuplé).
    const showTopDishes = customer.topDishes.length > 0;

    return (
        <div className="max-w-5xl mx-auto animate-fade-in-up">
            <button onClick={onBack} className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour à la liste
            </button>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {/* Header */}
                <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-gray-100">
                    <div className="flex items-center gap-4">
                        <Avatar customer={customer} />
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{customer.name}</h1>
                            <div className="mt-1 flex items-center flex-wrap gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${palette.bg} ${palette.text} ring-1 ring-inset ${palette.ring}`}>
                                    {segmentLabel(customer.segment)}
                                </span>
                                {formatMembershipDuration(customer.joinedDate) && (
                                    <span className="text-xs text-gray-500">{formatMembershipDuration(customer.joinedDate)}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-gray-400 font-semibold">Score fidélité</p>
                        <p className="text-3xl text-gray-900 dark:text-gray-100 font-stat">{customer.loyaltyScore}</p>
                        <p className="text-xs text-gray-400 mt-0.5">≠ segment — c'est le solde de points</p>
                    </div>
                </div>

                {/* 4 tuiles : Dernière visite, Total visites, Panier moyen, Total dépensé */}
                <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Tile label="Dernière visite" value={formatLastVisit(customer)} />
                    <Tile label="Total visites" value={customer.totalVisits} sub={`${customer.visitsLast90Days} sur 90 j`} />
                    <Tile
                        label="Panier moyen"
                        value={customer.averageBasket != null ? `${customer.averageBasket.toFixed(2)} €` : '—'}
                        sub={customer.averageBasket == null ? 'Aucun ticket renseigné' : undefined}
                    />
                    <Tile
                        label="Total dépensé"
                        value={customer.totalSpent != null ? `${customer.totalSpent.toFixed(2)} €` : '—'}
                        sub={customer.totalSpent == null ? 'Aucun ticket renseigné' : undefined}
                    />
                </div>

                {/* Sections conditionnelles */}
                <div className="px-6 pb-6 space-y-6">
                    {last6Months && (
                        <section>
                            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3 inline-flex items-center gap-2"><ChartLineIcon className="w-4 h-4 text-brand-primary" /> Activité 6 derniers mois</h2>
                            <ActivityBars data={last6Months} />
                        </section>
                    )}

                    {habitsAvailable && customer.totalVisits >= 5 && (
                        <section>
                            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2 inline-flex items-center gap-2"><ClockIcon className="w-4 h-4 text-brand-primary" /> Habitudes</h2>
                            <p className="text-sm text-gray-700">
                                {customer.preferredDay && <>Vient surtout le <strong>{customer.preferredDay}</strong>. </>}
                                {customer.preferredService && <>Service préféré : <strong>{customer.preferredService}</strong>.</>}
                            </p>
                        </section>
                    )}

                    {showTopDishes && (
                        <section>
                            <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2 inline-flex items-center gap-2"><UtensilsIcon className="w-4 h-4 text-brand-primary" /> Plats les plus commandés</h2>
                            <ol className="space-y-1.5">
                                {customer.topDishes.map((dish, i) => (
                                    <li key={dish} className="flex items-center gap-3 text-sm">
                                        <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">
                                            {i + 1}
                                        </span>
                                        <span className="text-gray-800">{dish}</span>
                                    </li>
                                ))}
                            </ol>
                        </section>
                    )}

                    <section>
                        <h2 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-3 inline-flex items-center gap-2"><ScrollIcon className="w-4 h-4 text-brand-primary" /> Historique des visites</h2>
                        <VisitsHistory customerId={customer.id} />
                    </section>
                </div>
            </div>
        </div>
    );
};

export default CustomerProfile;
